import React from 'react';
import styled from 'styled-components';

import {
  hideNativeScrollbar,
  overflowShadow,
  overflowShadowTransition,
  OverlayScrollbar,
  OverlayScrollWrapper,
  useOverlayScrollbar,
} from '../components/OverlayScrollbar';
import { truncateText } from './utils/mixins';
import { useSidebarCtx } from './SidebarContext';

// ============================================================
//     TYPES
// ============================================================
interface ContentProps extends React.ComponentPropsWithoutRef<'div'> {}

// ============================================================
//     STYLES
// ============================================================
const StyledContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--cl-space-4)',
  flex: '0 0 auto',

  width: '100%',
  paddingBlock: 'var(--cl-space-1)',
  paddingInline: 'var(--cl-space-4)',

  ...truncateText,
  overflow: 'clip',

  transitionProperty: 'width',
  transitionDuration: 'var(--cl-duration-300)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',
});

const StyledBody = styled(StyledContent)({
  flex: '1 1 auto',
  minHeight: '100%',
  paddingBlock: 'var(--cl-space-3)',
});

const StyledFooter = styled(StyledContent)({
  ...overflowShadowTransition,

  '[data-component="Sidebar.ScrollWrapper"][data-overflowing="true"] + &': {
    boxShadow: overflowShadow,
  },
});

const StyledScrollWrapper = styled(OverlayScrollWrapper)({
  flex: '1 1 auto',
  minHeight: 0,
});

const StyledScrollViewport = styled.div({
  height: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',

  // Native scrollbar is hidden entirely — the sibling `OverlayScrollbar`
  // renders the visible affordance instead, without reserving any layout width.
  ...hideNativeScrollbar,
});

// ============================================================
//     COMPONENTS
// ============================================================
/**
 * Fixed top section of the sidebar for persistent header content.
 *
 * Typically used for logos, account switchers, or primary navigation controls that should remain visible regardless of scroll position.
 */
export const Header = React.forwardRef<HTMLDivElement, ContentProps>(
  (props, forwardedRef) => {
    const { isExpanded } = useSidebarCtx();
    return (
      <StyledContent
        {...props}
        data-component="Sidebar.Header"
        data-expanded={isExpanded}
        ref={forwardedRef}
      />
    );
  },
);

Header.displayName = 'Sidebar.Header' as const;

/**
 * Fixed bottom section of the sidebar for persistent footer content.
 *
 * Typically used for user account controls, settings links, or secondary actions that should remain visible regardless of scroll position.
 */
export const Footer = React.forwardRef<HTMLDivElement, ContentProps>(
  (props, forwardedRef) => {
    const { isExpanded } = useSidebarCtx();
    return (
      <StyledFooter
        {...props}
        data-component="Sidebar.Footer"
        data-expanded={isExpanded}
        ref={forwardedRef}
      />
    );
  },
);

Footer.displayName = 'Sidebar.Footer' as const;

/**
 * Scrollable main content area of the sidebar.
 *
 * Wraps content in a vertical scroll container.
 * Use this for the primary navigation content that may overflow the viewport.
 */
export const Body = React.forwardRef<HTMLDivElement, ContentProps>(
  (props, forwardedRef) => {
    const { children, ...restProps } = props;
    const { isExpanded } = useSidebarCtx();
    const {
      handleScroll,
      isVisible,
      thumb,
      viewportRef,
      wrapperProps,
    } = useOverlayScrollbar();

    return (
      <StyledScrollWrapper
        {...wrapperProps}
        data-component="Sidebar.ScrollWrapper"
      >
        <StyledScrollViewport onScroll={handleScroll} ref={viewportRef}>
          <StyledBody
            {...restProps}
            data-component="Sidebar.Body"
            data-expanded={isExpanded}
            ref={forwardedRef}
          >
            {children}
          </StyledBody>
        </StyledScrollViewport>
        <OverlayScrollbar isVisible={isVisible} thumb={thumb} />
      </StyledScrollWrapper>
    );
  },
);

Body.displayName = 'Sidebar.Body' as const;
